<template>
  <div>
    <div v-if="!items.length && !loading" class="not-found-message">
      Nothing to display here :(
    </div>
    <div v-else>
      <div class="filter" v-if="filterProp">
        <el-input v-model="filterBy" :placeholder="filterPlaceholder" />
      </div>
        <el-button v-if="$route.path === '/product/experiments'" class="archive-button" size="mini" round @click="toggleArchived">{{ showArchived ? 'Hide Archived' : 'Show Archived' }}</el-button>
        <el-table
          v-loading="loading"
          stripe
          :data="showArchived ? archivedItems : filteredItems"
          max-height="500"
        >
        <div slot="empty">Nothing matches your filter...</div>

          <el-table-column
          v-for="column in columns"
          :key="column.name"
          :label="column.label"
          :prop="column.prop"
          :formatter="column.formatter"
        />

        <el-table-column label="Actions" v-if="actions && actions.length > 0">
          <template slot-scope="s">
            <div class="actions">
              <div
                class="action"
                v-for="action in actions.filter(a => !a.visible || a.visible(s.row))"
                :key="action.name"
              >
                <Confirm
                  v-if="action.requiresConfirm"
                  :title="action.title"
                  @confirm="() => action.onClick(s.row)"
                >
                  <el-button :type="action.type" size="mini">
                    {{ action.name }}
                  </el-button>
                </Confirm>
                <el-button
                  v-else
                  :type="action.type"
                  size="mini"
                  @click="action.onClick(s.row)"
                >
                  {{ action.name }}
                </el-button>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Links" v-if="links && links.length > 0">
          <template slot-scope="s">
            <template v-for="link in links.filter(l => !l.visible || l.visible(s.row))">
              <a
                v-if="link.href"
                :key="link.label"
                :href="link.href(s.row)"
                target="_blank"
                rel="noopener noreferrer"
                class="link"
              >
                {{ link.label }}
              </a>
              <router-link
                v-else
                :key="link.label"
                :to="link.to(s.row)"
                tag="a"
                class="link"
              >
                {{ link.label }}
              </router-link>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { get } from "lodash";
import Confirm from "@/components/Confirm";

export default {
  props: {
    items: Array,
    /*
     * columns is an array of objects with the following format:
     * label,    String - column's header
     * prop,     String - name of the relevant prop. can also be path (a.b.c => item[a][b][c])
     * display,  Function - a function that sets what will be displayed in that cell. params: this row's item
     */
    columns: Array,
    /*
     * actions is an array of objects with the following format:
     * name, String
     * type, String - 'primary' | 'succes' | 'info' | 'warning' | 'danger'
     * onClick, Function
     * requiresConfirm, Boolean
     */
    actions: Array,
    /*
     * links is an array of objects with the following format:
     * label, String
     * to, Function - given a row, returns an internal route
     * href, Function - given a row, returns an external URL
     * visible, Function - given a row, determines whether the link is shown
     */
    links: Array,
    filterProp: String,
    loading: Boolean,
  },
  created() {
    const { query } = this.$route;
    this.filterBy = query.q || "";
  },
  components: {
    Confirm,
  },
  data() {
    return {
      filterBy: "",
      showArchived: false,
    };
  },
  methods: {
    toggleArchived() {
      this.showArchived = !this.showArchived;
    }
  },  
  computed: {
    archivedItems() {
      return this.items.filter((item) => item.is_archived); 
    },
    filteredItems() {
      const { filterBy, filterProp } = this;
      const data = this.items;
      const activeExperiments = data.filter((item) => !item.is_archived);
      if (!filterBy) {
        return activeExperiments;
      }
      const filterRegex = new RegExp(filterBy, "i");
      return activeExperiments.filter((item) =>
        String(get(item, filterProp)).match(filterRegex)
      );
    },
    filterPlaceholder() {
      const relevantColumn = this.columns.find(
        (c) => c.prop === this.filterProp
      );
      const by = relevantColumn ? ` by ${relevantColumn.label}` : "";
      return `Filter${by}...`;
    },
  },
  watch: {
    "$route.query"(newQuery) {
      this.filterBy = newQuery.q || "";
    },
  },
};
</script>

<style scoped>
.not-found-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.filter {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: flex-start;
}

.link + .link {
  margin-left: 5px;
}

.link {
  color: #409eff;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: all ease-out 0.2s;
}

.link:hover {
  border-bottom: 1px solid #409eff;
}

.column-limited-width {
  width: 100px;
}

.archive-button {
  margin: 1rem 0;
}

</style>
